import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveDirective as _resolveDirective, createBlock as _createBlock, withDirectives as _withDirectives } from "vue"

import { computed, PropType } from 'vue';
import { hash8 } from '@/utils/misc-utils';
import {
  BAlert,
  BBadge,
  BButton,
  BButtonGroup,
  BTable,
  BTooltip,
} from 'bootstrap-vue-next';
import { useUserStore } from '@/store/user';

export type TaskFileType = {
  name: string;
  path: string;
  type: string;
  fileType: string;
  description: string;
};


export default /*@__PURE__*/_defineComponent({
  __name: 'ComputeTaskFilesList',
  props: {
  files: { type: Array as PropType<TaskFileType[]> },
},
  emits: ["directory-info-requested", "directory-view-requested", "file-preview-requested", "file-download-requested"],
  setup(__props, { emit: __emit }) {

const userStore = useUserStore();

// --- Component Properties ---
const props = __props;

const files = computed((): TaskFileType[] => {
  // keep only well-defined files
  let allFiles = props.files || [];
  allFiles = allFiles.filter(
    (file: any): file is TaskFileType =>
      file.name !== undefined && file.name !== null
  );

  // hide debug files for non-developers
  if (!userStore.developer) {
    allFiles = allFiles.filter(
      (file: TaskFileType) => file.type !== 'debug' && file.fileType !== 'log'
    );
  }

  // sort by type [data, report, figure, debug, ...], then name
  const typeOrder = ['data', 'report', 'figure', 'debug'];
  // add the remaining types at the end
  allFiles.forEach((file) => {
    if (!typeOrder.includes(file.type)) {
      typeOrder.push(file.type);
    }
  });

  return allFiles.sort((a, b) => {
    const aTypeIndex = typeOrder.indexOf(a.type);
    const bTypeIndex = typeOrder.indexOf(b.type);
    if (aTypeIndex !== bTypeIndex) {
      return aTypeIndex - bTypeIndex;
    }
    return a.name.localeCompare(b.name);
  });
});

const noFiles = computed(() => {
  return files.value.length === 0;
});

const fields = [
  { key: 'type', label: 'Files', tdClass: 'align-middle text-end min-width' },
  { key: 'name', label: '', tdClass: 'align-middle' },
  { key: 'actions', label: '', tdClass: 'align-middle text-end min-width' },
];

function getFileBadgeVariant(file: TaskFileType): string {
  if (file.fileType === 'directory') {
    return 'secondary';
  } else if (file.type === 'log') {
    return 'secondary';
  } else if (file.type === 'debug') {
    return 'danger';
  } else if (file.type === 'report') {
    return 'primary';
  } else if (file.type === 'figure') {
    return 'info';
  } else if (file.type === 'data') {
    return 'warning';
  }

  // fallback icon
  return 'secondary';
}

function getFileIcon(file: TaskFileType): [string[], string] {
  if (file.fileType === 'directory') {
    return [['bi', 'bi-folder'], ''];
  } else if (file.fileType === 'log') {
    return [['bi', 'bi-file-earmark-text'], ''];
  } else if (file.fileType === 'pdf') {
    //return [['material-symbols-outlined'], 'picture_as_pdf'];
    //return [['bi', 'bi-filetype-pdf'], ''];
    return [['bi', 'bi-file-earmark-pdf'], ''];
  } else if (file.fileType === 'html') {
    //return [['material-symbols-outlined'], 'HTML'];
    //return [['bi', 'bi-filetype-html'], ''];
    //return [['far', 'fa-file-code'], ''];
    return [['bi', 'bi-file-earmark-text'], ''];
  } else if (file.fileType === 'zip') {
    return [['bi', 'bi-archive'], ''];
  } else if (file.type === 'figure') {
    return [['bi', 'bi-graph-up'], ''];
  } else if (file.type === 'data') {
    return [['bi', 'bi-archive'], ''];
  } else if (file.type === 'report') {
    return [['bi', 'bi-file-earmark-text'], ''];
  } else if (file.type === 'debug') {
    return [['bi', 'bi-file-earmark-code'], ''];
  }

  // fallback icon
  return [['bi', 'bi-file-earmark'], ''];
}

function filesTableRowClicked(file: TaskFileType) {
  if (file.fileType === 'directory') {
    emits('directory-info-requested', file);
  } else {
    emits('file-preview-requested', file);
  }
}

// --- Emits ---
const emits = __emit;

return (_ctx: any,_cache: any) => {
  const _component_font_awesome_icon = _resolveComponent("font-awesome-icon")!
  const _directive_b_tooltip = _resolveDirective("b-tooltip")!

  return (_openBlock(), _createElementBlock("div", null, [
    _createVNode(_unref(BAlert), {
      modelValue: noFiles.value,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((noFiles).value = $event)),
      class: "mb-0",
      variant: "secondary",
      size: "sm"
    }, {
      default: _withCtx(() => [...(_cache[1] || (_cache[1] = [
        _createElementVNode("small", null, "No files available.", -1)
      ]))]),
      _: 1
    }, 8, ["modelValue"]),
    (!noFiles.value)
      ? (_openBlock(), _createBlock(_unref(BTable), {
          key: 0,
          items: files.value,
          fields: fields,
          small: "",
          striped: "",
          hover: "",
          responsive: "sm",
          stacked: "sm",
          class: "browser-table mb-0",
          onRowClicked: filesTableRowClicked
        }, {
          "cell(type)": _withCtx(({ item }) => [
            _createVNode(_unref(BBadge), {
              variant: getFileBadgeVariant(item),
              class: "file-badge"
            }, {
              default: _withCtx(() => [
                _createElementVNode("i", {
                  class: _normalizeClass(getFileIcon(item)[0])
                }, _toDisplayString(getFileIcon(item)[1]), 3),
                _createTextVNode(" " + _toDisplayString(item.type.toLowerCase()) + "/" + _toDisplayString(item.fileType.toLowerCase()), 1)
              ]),
              _: 2
            }, 1032, ["variant"])
          ]),
          "cell(name)": _withCtx(({ item }) => [
            _createElementVNode("span", null, _toDisplayString(item.name), 1),
            (item.description)
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                  _createVNode(_component_font_awesome_icon, {
                    class: "ms-2 text-secondary",
                    icon: "question-circle",
                    id: `file-description-${_unref(hash8)(item.path)}`
                  }, null, 8, ["id"]),
                  _createVNode(_unref(BTooltip), {
                    target: `file-description-${_unref(hash8)(item.path)}`,
                    triggers: "hover",
                    placement: "top",
                    class: "large-tooltip"
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(item.description), 1)
                    ]),
                    _: 2
                  }, 1032, ["target"])
                ], 64))
              : _createCommentVNode("", true)
          ]),
          "cell(actions)": _withCtx(({ item }) => [
            _createVNode(_unref(BButtonGroup), { size: "sm" }, {
              default: _withCtx(() => [
                (item.fileType === 'directory')
                  ? _withDirectives((_openBlock(), _createBlock(_unref(BButton), {
                      key: 0,
                      onClick: ($event: any) => (emits('directory-info-requested', item)),
                      variant: "outline-secondary",
                      title: "Show details"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_font_awesome_icon, { icon: "info-circle" })
                      ]),
                      _: 1
                    }, 8, ["onClick"])), [
                      [
                        _directive_b_tooltip,
                        void 0,
                        void 0,
                        { hover: true }
                      ]
                    ])
                  : _createCommentVNode("", true),
                (item.fileType === 'directory')
                  ? _withDirectives((_openBlock(), _createBlock(_unref(BButton), {
                      key: 1,
                      onClick: ($event: any) => (emits('directory-view-requested', item)),
                      variant: "outline-secondary",
                      title: "Browse directory"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_font_awesome_icon, { icon: "folder-open" })
                      ]),
                      _: 1
                    }, 8, ["onClick"])), [
                      [
                        _directive_b_tooltip,
                        void 0,
                        void 0,
                        { hover: true }
                      ]
                    ])
                  : _createCommentVNode("", true),
                (item.fileType !== 'directory')
                  ? _withDirectives((_openBlock(), _createBlock(_unref(BButton), {
                      key: 2,
                      onClick: ($event: any) => (emits('file-preview-requested', item)),
                      variant: "outline-secondary",
                      title: "Show details"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_font_awesome_icon, { icon: "info-circle" })
                      ]),
                      _: 1
                    }, 8, ["onClick"])), [
                      [
                        _directive_b_tooltip,
                        void 0,
                        void 0,
                        { hover: true }
                      ]
                    ])
                  : _createCommentVNode("", true),
                (item.fileType !== 'directory')
                  ? _withDirectives((_openBlock(), _createBlock(_unref(BButton), {
                      key: 3,
                      onClick: ($event: any) => (emits('file-download-requested', item)),
                      variant: "outline-secondary",
                      title: "Download"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_font_awesome_icon, { icon: "download" })
                      ]),
                      _: 1
                    }, 8, ["onClick"])), [
                      [
                        _directive_b_tooltip,
                        void 0,
                        void 0,
                        { hover: true }
                      ]
                    ])
                  : _createCommentVNode("", true)
              ]),
              _: 2
            }, 1024)
          ]),
          _: 1
        }, 8, ["items"]))
      : _createCommentVNode("", true)
  ]))
}
}

})